import Image from 'next/image';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import { NextLink, Text } from '@/atoms';
import { Button } from '@/molecules';

import { useMatchMedia } from '@/lib/hooks';
import { removeBaseURL } from '@/lib/utils';

import type { TypeImageWordpress } from '@/components/types';
import type { ElementType, FunctionComponent } from 'react';

type TypeSliderCards = {
  id: string;
  className?: string;
  bloque: {
    headingType: ElementType;
    title?: string;
    subtitle?: string;
    cards: Array<{
      image: TypeImageWordpress;
      button: {
        label: string;
        link?: {
          url: string;
        };
        enabled: boolean;
      };
    }>;
  };
};

/**
 * SliderCards
 */
const SliderCards: FunctionComponent<TypeSliderCards> = ({
  id,
  bloque,
  className,
}: TypeSliderCards) => {
  const { matches: trimSpace } = useMatchMedia({
    query: '(min-width: 450px)',
  });

  const { matches: pagination } = useMatchMedia({
    query: '(min-width: 1160px)',
  });

  return (
    <div
      id={id}
      className={`u-wrapper flex flex-col gap-y-4 px-4 py-4 md:gap-y-8 md:py-8 ${className}`}
    >
      <Text as={bloque.headingType} className="u-headline u-headline--h1">
        {bloque.title}
      </Text>

      <p className="u-body u-body--m">{bloque.subtitle}</p>

      {/**
       * TODO: La versión que tenemos de Splide no tiene el prop Children ya que en React 17 se añadia automaticamente pero en la 18 no
       * Cuando actualicemos Splide a la última versión, podemos quitar el @ts-ignore
       * @eslint-disable-next-line @typescript-eslint/ban-ts-comment
       * @ts-expect-error types are not for react 18 */}
      <Splide
        key="splide_slider_cards"
        className="max-md:-mx-4 max-md:[&>div]:!px-4"
        options={{
          arrows: false,
          gap: '1.75rem',
          pagination: (bloque.cards.length > 3 && pagination) || !pagination,
          type: 'slide',
          drag: (bloque.cards.length > 3 && pagination) || !pagination,
          autoWidth: true,
          lazyLoad: 'sequential',
          focus: 'center',
          trimSpace: trimSpace,
          updateOnMove: true,
          classes: {
            pagination: 'flex items-center justify-center mt-2 gap-3',
            page: 'w-3 h-3 border border-[#5a5a5a] rounded-full [&.is-active]:bg-[#5a5a5a]',
          },
        }}
      >
        {bloque.cards?.map((card, index) => (
          <SplideSlide key={index}>
            <Image
              src={card.image.sourceUrl}
              alt={card.image.altText}
              width={card.image.mediaDetails.width}
              height={card.image.mediaDetails.height}
              draggable={false}
              className="object-cover object-center max-md:max-w-72"
            />

            <NextLink
              href={removeBaseURL(card.button?.link?.url ?? '/')}
              passHref
            >
              <Button
                as="a"
                variant="tertiary"
                size="large-full"
                className={`absolute inset-x-4 bottom-4 !h-11 !w-auto !rounded-full !text-xl ${
                  !card.button.enabled
                    ? 'pointer-events-none !bg-[#434343] !text-primary-400'
                    : '!text-typo-alternative'
                }`}
              >
                {card.button.label}
              </Button>
            </NextLink>
          </SplideSlide>
        ))}
      </Splide>
    </div>
  );
};

SliderCards.displayName = 'SliderCards';

export default SliderCards;
